<template>
    <div>
        <b-table :items="tableItems" :fields="tableFields" class="mb-0" small bordered>
            <template #cell(number)="{ item, index }">
                <b-input v-model="editingRowData.number" v-if="editingRowIndex === index" autofocus
                    placeholder="Phone number" />
                <template v-else>{{ item.value.number }}</template>
            </template>
            <template #cell(reason)="{ item, index }">
                <b-input v-model="editingRowData.reason" v-if="editingRowIndex === index"
                    placeholder="Reason (optional)" />
                <template v-else>{{ item.value.reason }}</template>
            </template>
            <template #cell(actions)="{ item, index }">
                <div class="d-flex" style="gap: 6px;">
                    <template v-if="editingRowIndex !== index">
                        <b-button variant="link" @click.prevent="startTableEdit(item.value, index)" title="Edit">
                            <b-icon icon="pencil" variant="primary" />
                        </b-button>
                        <b-button variant="link" @click.prevent="removeTableItem(index)" title="Remove">
                            <b-icon icon="x-circle-fill" variant="danger" />
                        </b-button>
                    </template>
                    <template v-else>
                        <b-button variant="link" title="Save" @click.prevent="saveTableEdit(index)"
                            :disabled="!editingRowData.number?.trim()">
                            <b-icon icon="save" variant="primary" />
                        </b-button>
                        <b-button variant="link" title="Cancel Edit" @click.prevent="cancelTableEdit">
                            <b-icon icon="x-lg" variant="" />
                        </b-button>
                    </template>
                </div>
            </template>
        </b-table>

        <b-button size="sm" variant="primary" @click="addTableItem">
            Add
        </b-button>
    </div>
</template>

<script lang="ts">
import { Vue, Prop, Component, Watch } from "vue-property-decorator";
import { ForbiddenPhoneNumber } from "@/models/Configuration";

@Component({
    name: "ForbiddenPhoneNumbersField",
})
export default class ForbiddenPhoneNumbersField extends Vue {
    @Prop({ default: () => [] }) value!: ForbiddenPhoneNumber[];

    editingRowData: ForbiddenPhoneNumber = { number: "", reason: "" };
    editingRowIndex: number = -1;
    localValue: ForbiddenPhoneNumber[] = [];

    @Watch('value', { immediate: true, deep: true })
    onValueChange(newValue: ForbiddenPhoneNumber[]) {
        this.localValue = [...(newValue || [])];
    }

    get tableItems() {
        return this.localValue.map((ir, ix) => ({ index: ix, value: ir }));
    }

    get tableFields() {
        return [
            { key: 'number', label: 'Phone Number' },
            { key: 'reason', label: 'Reason' },
            { key: 'actions', label: '', class: 'actions' },
        ];
    }

    startTableEdit(item: ForbiddenPhoneNumber, index: number) {
        this.editingRowIndex = index;
        this.editingRowData = { ...item };
    }

    addTableItem() {
        if (this.editingRowIndex >= 0) {
            this.saveTableEdit(this.editingRowIndex);
        }

        const cleanedArray = this.localValue.filter((a) => a.number.trim());
        cleanedArray.push({ number: "", reason: "" });
        this.localValue = cleanedArray;
        this.$emit('input', [...this.localValue]);
        this.startTableEdit({ number: "", reason: "" }, cleanedArray.length - 1);
    }

    removeTableItem(index: number) {
        const array = [...this.localValue];
        array.splice(index, 1);
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
    }

    cancelTableEdit() {
        const editingIndex = this.editingRowIndex;
        const editingValue = this.localValue[editingIndex]?.number;
        if (!editingValue) {
            this.removeTableItem(editingIndex);
        }
        this.editingRowIndex = -1;
        this.editingRowData = { number: "", reason: "" };
    }

    saveTableEdit(index: number) {
        const array = [...this.localValue];
        const trimmedNumber = this.editingRowData.number?.trim();
        if (!trimmedNumber) {
            array.splice(index, 1);
        } else {
            array.splice(index, 1, {
                number: trimmedNumber,
                reason: this.editingRowData.reason?.trim() || "",
            });
        }
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
        this.editingRowIndex = -1;
        this.editingRowData = { number: "", reason: "" };
    }
}
</script>

<style scoped lang="scss">
::v-deep(thead th),
::v-deep(td) {
    padding: 1px 7px !important;
}

::v-deep(td) {
    height: 38px !important;
}

table ::v-deep(.actions) {
    width: 90px;
}
</style>
